<template>
  <div id="app" :class="appClasses">
    <div class="main-wrapper">
      <router-view />
    </div>
    <ai-assistant-widget v-if="showAssistant" />
    <app-footer v-if="showFooter" />
  </div>
</template>

<script>
import AppFooter from "@/components/layout/AppFooter.vue";
import AiAssistantWidget from "@/components/ai/AiAssistantWidget.vue";
import { getCurrentUser } from "@/services/authService";

export default {
  components: {
    AppFooter,
    AiAssistantWidget,
  },
  computed: {
    appClasses() {
      const role = String(getCurrentUser()?.role || "guest").toLowerCase();
      const route = String(this.$route.path || "/")
        .replace(/^\/+|\/+$/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "-") || "home";
      return [`role-${role}`, `route-${route}`];
    },
    showFooter() {
      const hiddenRoutes = ["/", "/signup"];
      return !hiddenRoutes.includes(this.$route.path);
    },
    showAssistant() {
      const hasSession = Boolean(localStorage.getItem("access_token"));
      const publicRoutes = ["/", "/signup", "/forgot-password"];
      return hasSession && !publicRoutes.includes(this.$route.path) && !this.$route.path.startsWith("/reset-password/");
    },
  },
};
</script>

<style scoped>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f6fa;
}

.main-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Deletion is an administrator-only action. The API enforces the same rule. */
.role-user :deep(.confirm-text),
.role-user :deep(.btn-outline-danger),
.role-user :deep(a:has(.feather-trash-2)),
.role-user :deep(button:has(.feather-trash-2)),
.role-user :deep(a:has([data-feather="trash-2"])),
.role-user :deep(button:has([data-feather="trash-2"])) {
  display: none !important;
}

/* Products, services and stock are read-only for employees. */
.role-user.route-produits-list-produits :deep(.page-btn),
.role-user.route-produits-list-produits :deep(a[href="/produits/modification-produit"]),
.role-user.route-services :deep(.page-btn),
.role-user.route-services :deep(a[href="/produits/modification-service"]),
.role-user.route-stocks :deep(.page-btn),
.role-user.route-stocks :deep(a[data-bs-target="#edit-units"]) {
  display: none !important;
}
</style>

<style>
html[dir="rtl"] body {
  direction: rtl;
  text-align: right;
}

html[dir="rtl"] .sidebar {
  left: auto;
  right: 0;
}

html[dir="rtl"] .page-wrapper {
  margin-left: 0;
  margin-right: 260px;
}

html[dir="rtl"] .header-left {
  border-left: 1px solid #e8ebed;
  border-right: 0;
  float: none !important;
  left: auto !important;
  position: absolute !important;
  right: 0 !important;
}

html[dir="rtl"] .header .user-menu {
  direction: ltr;
  float: none !important;
  left: 18px;
  margin: 0 !important;
  position: absolute;
  right: auto !important;
  top: 5px;
}

html[dir="rtl"] .header .user-menu > li {
  direction: ltr;
}

html[dir="rtl"] .header-left #toggle_btn {
  left: -16px;
  right: auto;
}

html[dir="rtl"] .header-left .logo,
html[dir="rtl"] .header-left .logo-small {
  margin-left: auto;
  margin-right: auto;
}

html[dir="rtl"] .header .main-drop .userset {
  padding-left: 8px;
  padding-right: 8px;
}

html[dir="rtl"] .header .language-dropdown,
html[dir="rtl"] .header .notifications,
html[dir="rtl"] .header .menu-drop-user {
  direction: rtl;
  text-align: right;
}

html[dir="rtl"] .menu-arrow {
  left: 15px;
  right: auto;
  transform: rotate(180deg);
}

/* Guard against scoped RTL icon rules being compiled as a transform on the
   document root. Only individual directional icons should ever be rotated. */
html[dir="rtl"] {
  transform: none !important;
}

/* Keep sidebar labels clearly separated from their icons in both directions. */
.sidebar .sidebar-menu > ul > li > ul > li > a {
  column-gap: 12px;
}

.sidebar .sidebar-menu > ul > li > ul > li > a > svg:first-child {
  flex: 0 0 auto;
  margin-inline: 0 !important;
}

.sidebar .sidebar-menu > ul > li > ul > li > a > span:not(.menu-arrow) {
  line-height: 1.45;
}

@media (max-width: 991px) {
  html[dir="rtl"] .page-wrapper {
    margin-right: 0;
  }

  html[dir="rtl"] .header-left {
    left: auto !important;
    right: 55px !important;
  }

  html[dir="rtl"] .header .user-menu {
    left: 12px !important;
    right: auto !important;
  }

  html[dir="rtl"] .sidebar {
    left: auto !important;
    margin: 0 !important;
    right: -280px !important;
    transition: right 0.25s ease !important;
    width: 260px !important;
    z-index: 1041 !important;
  }

  html[dir="rtl"] body.slide-nav .sidebar {
    left: auto !important;
    right: 0 !important;
    box-shadow: -12px 0 32px rgba(15, 23, 42, 0.2);
  }

  html[dir="rtl"] body.slide-nav .page-wrapper,
  html[dir="rtl"] .page-wrapper {
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
  }

  html[dir="rtl"] .header .mobile_btn {
    left: auto !important;
    right: 8px !important;
    width: 42px !important;
    z-index: 1050;
  }

  html[dir="rtl"] .header-left {
    left: auto !important;
    right: 50px !important;
    width: 138px !important;
  }

  html[dir="rtl"] .header-left .logo {
    justify-content: flex-start;
    padding: 0 6px;
  }

  html[dir="rtl"] .header-left .logo img {
    max-height: 36px;
    max-width: 118px;
  }

  html[dir="rtl"] .header .user-menu {
    gap: 2px;
    left: 6px !important;
    max-width: calc(100% - 194px);
  }

  html[dir="rtl"] .header .user-menu .nav-item-box > a,
  html[dir="rtl"] .header .language-trigger {
    height: 38px;
    min-width: 38px;
    padding: 7px !important;
  }

  html[dir="rtl"] .header .user-menu .main-drop .user-info {
    display: none !important;
  }

  html[dir="rtl"] .header .user-menu .user-avatar-img {
    height: 32px;
    width: 32px;
  }
}

@media (max-width: 575px) {
  html[dir="rtl"] .header .user-menu .nav-item-box:nth-of-type(2) {
    display: none !important;
  }

  html[dir="rtl"] .content,
  html[dir="rtl"] .page-wrapper .content {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
}

/* Shared RTL behavior for every popup, including teleported components. */
html[dir="rtl"] .modal,
html[dir="rtl"] .offcanvas,
html[dir="rtl"] .ant-modal,
html[dir="rtl"] .swal2-popup {
  direction: rtl;
  text-align: right;
}

html[dir="rtl"] .modal-header,
html[dir="rtl"] .modal-footer,
html[dir="rtl"] .ant-modal-header,
html[dir="rtl"] .ant-modal-footer {
  direction: rtl;
}

html[dir="rtl"] .modal-header .btn-close,
html[dir="rtl"] .modal-header .close {
  margin-left: 0;
  margin-right: auto;
}

html[dir="rtl"] .modal input,
html[dir="rtl"] .modal textarea,
html[dir="rtl"] .modal select,
html[dir="rtl"] .offcanvas input,
html[dir="rtl"] .offcanvas textarea,
html[dir="rtl"] .ant-modal input,
html[dir="rtl"] .ant-modal textarea {
  text-align: right;
}

html[dir="rtl"] .modal input[type="email"],
html[dir="rtl"] .modal input[type="tel"],
html[dir="rtl"] .modal input[type="number"],
html[dir="rtl"] .ant-modal input[type="email"],
html[dir="rtl"] .ant-modal input[type="tel"],
html[dir="rtl"] .ant-modal input[type="number"] {
  direction: ltr;
}

html[dir="rtl"] .modal-footer,
html[dir="rtl"] .ant-modal-footer,
html[dir="rtl"] .swal2-actions {
  flex-direction: row-reverse;
  justify-content: flex-start;
}

@media (max-width: 575px) {
  .modal-dialog {
    margin: 10px;
    max-width: calc(100% - 20px);
  }

  .modal-content {
    border-radius: 14px;
  }

  .modal-body,
  .modal-header,
  .modal-footer {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>
